// ===============================
// api.ts — dev-mode ready with mock API fallback
// ===============================
// Drop-in replacement for your current api.ts.
// In development, if Telegram WebApp is not present (or you force it),
// this will transparently switch to an in-memory mock API so the app works
// with `npm run dev` in a normal browser.
//
// Force rules (any one makes it use the mock in dev):
//   • ?mock=1 in the URL
//   • localStorage.setItem('mockApi', '1')
//   • VITE_FORCE_MOCK=1 in your environment
//
// You can also keep using the real API in dev by ensuring Telegram is present
// (e.g., within the Telegram WebApp) and not setting the above flags.

import { TROPHY_DEFINITIONS } from '@/data/trophies'

import type {
  ApiUser,
  GameState,
  LeaderboardEntry,
  PlayerProfile,
  PlayerSearchResult,
  RivalsResponse,
  FortuneApiResponse,
  FortuneDisplay,
  TaskItem,
  InventoryResponse,
  InventoryItem,
  MarketplaceResponse,
  MarketplaceListing,
  MarketplaceSort,
  SparklesBalance,

  SparklesHistory,
  PreparedShareRequest,
  PreparedShareResponse,
} from '@/types'

// ---- Environment helpers
const API_BASE: string = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_BASE)
  || 'https://fortune.poc.workers.dev'

const IS_DEV: boolean =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV) ??
  (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production')

const HAS_TG: boolean =
  typeof window !== 'undefined' && !!window.Telegram?.WebApp

// Telegram exists but has no signed initData → treat as dev/mock
const TG_INIT_EMPTY: boolean =
  typeof window !== 'undefined' &&
  !!window.Telegram?.WebApp &&
  (!window.Telegram.WebApp.initData || window.Telegram.WebApp.initData === '')

// Allow a global flag from the polyfill to explicitly mark it as mock
const TG_IS_MOCK_FLAG: boolean =
  typeof window !== 'undefined' && !!(window as any).__TG_WEBAPP_MOCK

const FORCE_MOCK: boolean =
  typeof window !== 'undefined' &&
  (
    new URLSearchParams(window.location.search).get('mock') === '1' ||
    window.localStorage?.getItem?.('mockApi') === '1' ||
    ((typeof import.meta !== 'undefined') && (import.meta as any).env?.VITE_FORCE_MOCK === '1')
  )

// 👇 NEW: auto-mock in dev if there's no Telegram OR Telegram is clearly mock/empty
const USE_MOCK: boolean = !!(
  IS_DEV && (FORCE_MOCK || !HAS_TG || TG_INIT_EMPTY || TG_IS_MOCK_FLAG)
)

export const isMockApi = USE_MOCK


// ===============================
// Real API service (unchanged behavior)
// ===============================
class RealApiService {
  getBaseUrl(): string {
    return API_BASE
  }

  private async request<T>(
    path: string,
    method: 'GET' | 'POST' = 'GET',
    body?: unknown
  ): Promise<T> {
    const headers: Record<string, string> = {}
    if (typeof window !== 'undefined') {
      headers['X-TG-Init'] = window.Telegram?.WebApp?.initData ?? ''
    }

    if (body !== undefined) {
      headers['Content-Type'] = 'application/json'
    }

    const response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({} as any))
      
      // Create enhanced error object with debug info
      const error = new Error((errorData as any).error || response.statusText) as any
      error.status = response.status
      error.data = errorData
      error.path = path
      error.method = method
      
      // Log initData errors for debugging
      if (errorData?.error?.includes('INITDATA') || errorData?.error?.includes('INVALID_INITDATA')) {
        console.warn('InitData validation error:', {
          error: errorData.error,
          debug: errorData.debug,
          path,
          method,
          status: response.status,
          initDataLength: headers['X-TG-Init']?.length || 0,
          hasInitData: !!headers['X-TG-Init']
        })
      }
      
      throw error
    }

    return response.json()
  }

  private normalizeUser(user: ApiUser): GameState {
    const today = new Date().toISOString().slice(0, 10) // YYYY-MM-DD
    const canWatchRewardedAd = user.last_ad_reward !== today
    
    return {
      cracksLeft: user.cracks_left,
      totalCracked: user.total_cracked,
      refCode: user.ref_code,
      referredBy: user.referred_by,
      refEarned: user.earned_from_refs ?? 0,
      lastPlayed: user.last_played ?? null,
      streakCur: user.streak_cur ?? 0,
      streakBest: user.streak_best ?? 0,
      streakTodayCounted: !!user.streak_today_counted,
      canWatchRewardedAd,
    }
  }

  async getStatus(userId: string): Promise<GameState> {
    const user = await this.request<ApiUser>(`/status/${userId}`)
    return this.normalizeUser(user)
  }

  async generateFortune(userId: string): Promise<FortuneApiResponse> {
    return this.request<FortuneApiResponse>(`/fortune/${userId}`)
  }

  async getFortune(userId: string): Promise<{ fortune: FortuneDisplay; state: GameState }> {
    const res = await this.request<{ data: FortuneApiResponse['data']; state?: ApiUser }>(
      `/fortune/${userId}`,
      'POST'
    )

    // flatten rarity -> FortuneDisplay
    const fd: FortuneDisplay = {
      message: res.data.message,
      tier: res.data?.rarity?.tier ?? 'common',
      stars: Number(res.data?.rarity?.stars ?? 1),
    }

    // prefer atomic state from server; fallback to status
    const freshUser = res.state ?? await this.request<ApiUser>(`/status/${userId}`)
    return { fortune: fd, state: this.normalizeUser(freshUser) }
  }



  async getLeaderboard(): Promise<LeaderboardEntry[]> {
    return this.request<LeaderboardEntry[]>('/leaderboard')
  }

  async getRivals(userId: string, n: number = 5): Promise<RivalsResponse> {
    return this.request<RivalsResponse>(`/rivals/${userId}?n=${n}`)
  }

  async searchPlayers(query: string, limit: number = 20): Promise<PlayerSearchResult[]> {
    const trimmed = query.trim()
    const params = new URLSearchParams({
      q: trimmed,
      limit: String(limit),
    })
    const response = await this.request<{ results: PlayerSearchResult[] }>(
      `/players?${params.toString()}`
    )
    return response.results
  }

  async getPlayerProfile(
    id: string,
    options: { limit?: number; offset?: number; sort?: string } = {}
  ): Promise<PlayerProfile> {
    const params = new URLSearchParams()
    if (options.limit != null) params.set('limit', String(options.limit))
    if (options.offset != null) params.set('offset', String(options.offset))
    if (options.sort) params.set('sort', options.sort)
    const query = params.toString()
    const path = query ? `/profile/${id}?${query}` : `/profile/${id}`
    return this.request<PlayerProfile>(path)
  }

  async createInvoice(userId: string, packs: number = 1): Promise<{ invoice_url: string }> {
    return this.request<{ invoice_url: string }>(`/invoice/${userId}?packs=${packs}`)
  }

  async createFeatureInvoice(userId: string, feature: string): Promise<{ invoice_url: string }> {
    return this.request<{ invoice_url: string }>(`/feature-invoice/${userId}?feature=${feature}`)
  }

  async createPreparedShareMessage(userId: string, payload: PreparedShareRequest): Promise<PreparedShareResponse> {
    return this.request<PreparedShareResponse>(`/share-message/${userId}`, 'POST', payload)
  }

  async attachReferral(userId: string, refCode: string): Promise<void> {
    await this.request(`/signup/${userId}?ref=${encodeURIComponent(refCode)}`, 'POST')
  }

  async getTasks(userId: string): Promise<TaskItem[]> {
    return this.request<TaskItem[]>(`/tasks/${userId}`)
  }

  async claimTask(userId: string, taskId: string): Promise<GameState> {
    const u = await this.request<ApiUser>(`/task-claim/${userId}/${taskId}`, 'POST')
    return this.normalizeUser(u)
  }

  async verifyTask(userId: string, taskId: string): Promise<GameState> {
    const u = await this.request<ApiUser>(`/task-verify/${userId}/${taskId}`, 'POST')
    return this.normalizeUser(u)
  }

  async getInventory(userId: string, limit: number = 20, offset: number = 0, sort: 'rarity' | 'recent' = 'recent'): Promise<InventoryResponse> {
    return this.request<InventoryResponse>(`/inventory/${userId}?limit=${limit}&offset=${offset}&sort=${sort}`)
  }

  async burnFortune(userId: string, mintId: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/burn/${userId}/${mintId}`, 'POST')
  }

  async claimRewardedAdReward(userId: string): Promise<string> {
    const response = await fetch(`${API_BASE}/reward/${userId}`, {
      method: 'GET',
      headers: {
        'X-TG-Init': typeof window !== 'undefined' ? (window.Telegram?.WebApp?.initData ?? '') : '',
        'Content-Type': 'application/json'
      }
    })
    
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(errorText || `HTTP ${response.status}`)
    }
    
    return await response.text()
  }

  async getMarketplaceListings(
    limit: number = 20,
    offset: number = 0,
    sort: MarketplaceSort = 'newest',
    seller?: string
  ): Promise<MarketplaceResponse> {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      sort,
    })
    if (seller) {
      params.set('seller', seller)
    }
    return this.request<MarketplaceResponse>(`/marketplace?${params.toString()}`)
  }

  async listFortuneForSale(userId: string, mintId: string, priceStars: number): Promise<MarketplaceListing> {
    const res = await this.request<{ listing: MarketplaceListing }>(
      `/marketplace-list/${userId}`,
      'POST',
      { mint_id: mintId, price_stars: priceStars }
    )
    return res.listing
  }

  async cancelListing(userId: string, listingId: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/marketplace-unlist/${userId}/${listingId}`, 'POST')
  }

  async getMarketplaceInvoice(userId: string, listingId: string): Promise<{ invoice_url: string }> {
    return this.request<{ invoice_url: string }>(
      `/marketplace-invoice/${userId}?listing=${encodeURIComponent(listingId)}`
    )
  }

  // Sparkles API
  async getSparklesBalance(userId: string): Promise<SparklesBalance> {
    return this.request<SparklesBalance>(`/sparkles/${userId}/balance`)
  }

  async createSparklesInvoice(userId: string, starsAmount: number): Promise<{ invoice_link: string }> {
    return this.request<{ invoice_link: string }>(
      `/sparkles/${userId}/purchase`,
      'POST',
      { stars: starsAmount }
    )
  }

  async getSparklesHistory(userId: string, limit = 20, offset = 0): Promise<SparklesHistory> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    })
    return this.request<SparklesHistory>(`/sparkles/${userId}/history?${params.toString()}`)
  }

  // Sparkles marketplace functions
  async listFortuneForSparkles(userId: string, mintId: string, priceSparkles: number): Promise<MarketplaceListing> {
    const res = await this.request<{ listing: MarketplaceListing }>(
      `/marketplace-list-sparkles/${userId}`,
      'POST',
      { mint_id: mintId, price_sparkles: priceSparkles }
    )
    return res.listing
  }

  async purchaseListingWithSparkles(userId: string, listingId: string): Promise<{ ok: boolean; message: string }> {
    return this.request<{ ok: boolean; message: string }>(
      `/marketplace-buy-sparkles/${userId}`,
      'POST',
      { listing_id: listingId }
    )
  }
}

// ===============================
// Mock API service (dev only)
// ===============================

// Lightweight pseudo-database in memory (optionally backed by localStorage)
interface MockUser extends ApiUser {
  tasks?: TaskItem[]
  inventory?: InventoryItem[]
}

interface MockListing extends MarketplaceListing {
  active: boolean
}

const LS_KEY = 'mockApiDb:v1'
const LS_LISTINGS_KEY = 'mockApiListings:v1'

interface MockPlayerProfile {
  id: string
  telegram_id: string
  display: string
  username: string
  total_cracked: number
}

function loadDb(): Record<string, MockUser> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(LS_KEY)
    return raw ? (JSON.parse(raw) as Record<string, MockUser>) : {}
  } catch {
    return {}
  }
}

function saveDb(db: Record<string, MockUser>) {
  if (typeof window === 'undefined') return
  try { window.localStorage.setItem(LS_KEY, JSON.stringify(db)) } catch {}
}

const mockDb: Record<string, MockUser> = loadDb()

function loadListings(): Record<string, MockListing> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(LS_LISTINGS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, MockListing>
    return Object.fromEntries(
      Object.entries(parsed).map(([id, listing]) => [id, { ...listing }])
    )
  } catch {
    return {}
  }
}

function saveListings(listings: Record<string, MockListing>) {
  if (typeof window === 'undefined') return
  try { window.localStorage.setItem(LS_LISTINGS_KEY, JSON.stringify(listings)) } catch {}
}

const mockListings: Record<string, MockListing> = loadListings()

const mockPlayerPool: MockPlayerProfile[] = []

function ensureMockPlayerPool(): MockPlayerProfile[] {
  if (mockPlayerPool.length === 0) {
    const adjectives = ['Swift', 'Lucky', 'Bright', 'Clever', 'Bold', 'Golden', 'Brave', 'Quick', 'Silent', 'Mighty']
    const animals = ['Fox', 'Panda', 'Tiger', 'Crane', 'Hawk', 'Otter', 'Wolf', 'Koi', 'Dragon', 'Phoenix']
    for (let i = 0; i < 120; i += 1) {
      const id = `user_${i + 1}`
      const adjective = adjectives[i % adjectives.length]
      const animal = animals[i % animals.length]
      const display = `${adjective} ${animal}`
      const username = `${animal.toLowerCase()}${i + 1}`
      const total_cracked = Math.max(5, 600 - i * 4 + Math.floor(Math.random() * 25))
      mockPlayerPool.push({ id, telegram_id: id, display, username, total_cracked })
    }
  }
  return mockPlayerPool
}

function syncUserToPlayerPool(user: MockUser): void {
  if (!user?.id) return
  const pool = ensureMockPlayerPool()
  const existing = pool.find(entry => entry.id === user.id)
  const suffix = String(user.id).slice(-4)
  const display = `Player ${suffix}`
  const username = `player${suffix}`
  if (existing) {
    existing.total_cracked = user.total_cracked
    if (!existing.telegram_id) existing.telegram_id = user.id
    if (!existing.display) existing.display = display
    if (!existing.username) existing.username = username
    return
  }
  pool.push({
    id: user.id,
    telegram_id: user.id,
    display,
    username,
    total_cracked: user.total_cracked,
  })
}

function todayKey(d = new Date()): string {
  return d.toISOString().slice(0, 10) // YYYY-MM-DD
}

function genRefCode(): string {
  return Math.random().toString(36).slice(2, 8).toUpperCase()
}

function ensureUser(userId: string): MockUser {
  if (!mockDb[userId]) {
    mockDb[userId] = {
      id: userId,
      last_reset: todayKey(),
      cracks_left: 10,
      total_cracked: 0,
      ref_code: `DEV-${genRefCode()}`,
      referred_by: null,
      earned_from_refs: 0,
      last_played: null,
      streak_cur: 0,
      streak_best: 0,
      streak_today_counted: false,
      tasks: undefined, // will be created lazily
      inventory: undefined,
    }
    saveDb(mockDb)
  }
  const user = mockDb[userId]
  ensureInventory(userId, user)
  syncUserToPlayerPool(user)
  return user
}

function normalize(u: MockUser): GameState {
  const today = new Date().toISOString().slice(0, 10) // YYYY-MM-DD
  const canWatchRewardedAd = u.last_ad_reward !== today
  
  return {
    cracksLeft: u.cracks_left,
    totalCracked: u.total_cracked,
    refCode: u.ref_code,
    referredBy: u.referred_by,
    refEarned: u.earned_from_refs ?? 0,
    lastPlayed: u.last_played ?? null,
    streakCur: u.streak_cur ?? 0,
    streakBest: u.streak_best ?? 0,
    streakTodayCounted: !!u.streak_today_counted,
    canWatchRewardedAd,
  }
}

function maybeDailyReset(u: MockUser) {
  const today = todayKey()
  if (u.last_reset !== today) {
    u.last_reset = today
    // Simple daily refill to keep dev moving
    u.cracks_left = Math.max(u.cracks_left, 10)
  }
}

const mockRarityTiers = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'limited'] as const
const mockRarityColors: Record<string, string> = {
  common: '#8B4513',
  uncommon: '#CD853F',
  rare: '#DAA520',
  epic: '#9932CC',
  legendary: '#FF4500',
  limited: '#0EA5E9',
}

const tierStarMap: Record<string, number> = {
  common: 1,
  uncommon: 2,
  rare: 3,
  epic: 4,
  legendary: 5,
  limited: 5,
}

function createMockInventoryItem(userId: string, index: number): InventoryItem {
  const messages = [
    'Fortune favors the bold.',
    'Your persistence will pay off.',
    'Great things await you.',
    'Trust your instincts.',
    'Success is within reach.',
    'A new opportunity approaches.',
    'Your hard work will be rewarded.',
    'Believe in yourself.',
    'Good fortune is coming your way.',
    'Stay positive and keep going.'
  ]

  const tierIndex = Math.min(Math.floor(index / 8), mockRarityTiers.length - 1)
  const rarity = mockRarityTiers[tierIndex]
  const messageIndex = (index + userId.charCodeAt(0)) % messages.length
  const mintedAt = new Date(Date.now() - index * 12 * 60 * 60 * 1000).toISOString()

  const stars = tierStarMap[rarity] ?? Math.min(tierIndex + 1, 5)

  return {
    mint_id: `mock-${userId}-${index}`,
    minted_at: mintedAt,
    source: 'crack',
    crack_total_after: index + 1,
    streak_cur_after: Math.min(index + 1, 7),
    fortune_id: `fortune-${index}`,
    message: messages[messageIndex],
    theme: 'default',
    tone: 'positive',
    rarity_tier: rarity,
    stars,
    p: tierIndex > 0 ? Math.round((Math.random() * 0.1 + 0.01) * 1000) / 1000 : null,
    color: mockRarityColors[rarity] ?? '#8B4513',
    season: 'dev',
    listing_id: null,
    listing_price_stars: null,
    listing_created_at: null,
  }
}

function ensureInventory(userId: string, u: MockUser): InventoryItem[] {
  if (!u.inventory) {
    const baseCount = Math.max(6, Math.min(u.total_cracked || 12, 40))
    u.inventory = Array.from({ length: baseCount }, (_, index) => createMockInventoryItem(userId, index))
    saveDb(mockDb)
  }
  return u.inventory
}

function countsFromInventoryItems(items: InventoryItem[]): Record<string, number> {
  return items.reduce<Record<string, number>>((acc, item) => {
    const rarity = (item.rarity_tier || 'unknown').toLowerCase()
    acc[rarity] = (acc[rarity] ?? 0) + 1
    return acc
  }, {})
}

function meetsTrophyRequirements(
  counts: Record<string, number>,
  requirements: Record<string, number>
): boolean {
  return Object.entries(requirements).every(([rarity, needed]) => {
    const owned = counts[rarity] ?? 0
    return owned >= Number(needed || 0)
  })
}

function getActiveTrophiesFromInventoryItems(items: InventoryItem[]): typeof TROPHY_DEFINITIONS {
  if (!items || items.length === 0) return []

  const counts = countsFromInventoryItems(items)
  return TROPHY_DEFINITIONS.filter(trophy => meetsTrophyRequirements(counts, trophy.requirements))
}

function ensureSeedListings(): void {
  if (Object.keys(mockListings).length > 0) return

  const sellerIds = ['demo_seller1', 'demo_seller2', 'demo_seller3']
  sellerIds.forEach((sellerId, idx) => {
    const seller = ensureUser(sellerId)
    const inventory = ensureInventory(sellerId, seller)
    if (inventory.length === 0) return
    const item = inventory[idx % inventory.length]
    const listingId = `seed-${idx + 1}`
    const createdAt = new Date(Date.now() - idx * 3600 * 1000).toISOString()

    item.listing_id = listingId
    item.listing_price_stars = 10 + idx * 5
    item.listing_created_at = createdAt

    mockListings[listingId] = {
      id: listingId,
      active: true,
      mint_id: item.mint_id,
      seller_id: sellerId,
      seller_username: `demo_${idx + 1}`,
      seller_display_name: `Demo Seller ${idx + 1}`,
      price_stars: item.listing_price_stars,
      created_at: createdAt,
      rarity_tier: item.rarity_tier,
      stars: item.stars,
      p: item.p,
      color: item.color,
      season: item.season,
      message: item.message,
      theme: item.theme,
      tone: item.tone,
    }
  })

  saveDb(mockDb)
  saveListings(mockListings)
}

function updateStreakOnCrack(u: MockUser) {
  const today = todayKey()
  if (u.last_played === today) {
    // already played today
    if (!u.streak_today_counted) {
      u.streak_today_counted = true
      u.streak_cur = Math.max(u.streak_cur || 1, 1)
      u.streak_best = Math.max(u.streak_best || 1, u.streak_cur)
    }
    return
  }

  if (u.last_played) {
    const prev = new Date(u.last_played)
    const diffDays = Math.floor((Date.parse(today) - Date.parse(prev.toISOString().slice(0,10))) / (24*3600*1000))
    if (diffDays === 1) {
      u.streak_cur = (u.streak_cur ?? 0) + 1
    } else {
      u.streak_cur = 1
    }
  } else {
    u.streak_cur = 1
  }
  u.streak_best = Math.max(u.streak_best ?? 0, u.streak_cur)
  u.last_played = today
  u.streak_today_counted = true
}

function buildTasks(u: MockUser): TaskItem[] {
  const tasks: TaskItem[] = []

  // Counter: every 10 cracks → claim +5 cracks
  const need10 = 10
  const progress10 = u.total_cracked % need10
  const counterTask: TaskItem = {
    id: 'counter:10',
    title: 'Crack 10 cookies',
    kind: 'counter',
    need: need10,
    reward: 5,
    progress: progress10,
    ready: progress10 === 0 && u.total_cracked > 0 && !(u as any)._counter10_claimed,
    claimed: !!(u as any)._counter10_claimed,
    needsVerify: false,
  }
  tasks.push(counterTask)

  // Streak: play 3 days in a row → +8 cracks
  const streakNeed = 3
  const streakTask: TaskItem = {
    id: 'streak:3',
    title: 'Play 3 days in a row',
    kind: 'streak',
    need: streakNeed,
    reward: 8,
    progress: Math.min(u.streak_cur ?? 0, streakNeed),
    ready: (u.streak_cur ?? 0) >= streakNeed && !(u as any)._streak3_claimed,
    claimed: !!(u as any)._streak3_claimed,
    needsVerify: false,
  }
  tasks.push(streakTask)

  // Join TG (dev: just verify)
  const joinTask: TaskItem = {
    id: 'tg:join_channel',
    title: 'Join our Telegram channel',
    kind: 'tg_member',
    need: null,
    reward: 10,
    progress: 0,
    ready: !(u as any)._tg_join_claimed && !!(u as any)._tg_join_verified,
    claimed: !!(u as any)._tg_join_claimed,
    needsVerify: !(u as any)._tg_join_verified,
    join_url: 'https://t.me/example',
  }
  tasks.push(joinTask)

  // Start bot (dev: claimable immediately)
  const startBot: TaskItem = {
    id: 'tg:bot_started',
    title: 'Start the Fortune bot',
    kind: 'bot_started',
    need: null,
    reward: 5,
    progress: 0,
    ready: !(u as any)._bot_started_claimed,
    claimed: !!(u as any)._bot_started_claimed,
    needsVerify: false,
    start_url: 'https://t.me/your_bot?start=dev',
  }
  tasks.push(startBot)

  // Vote for app in Telegram Apps Center
  const voteTask: TaskItem = {
    id: 'apps_center:vote',
    title: 'Vote for us in Telegram Apps Center',
    kind: 'apps_center_vote',
    need: null,
    reward: 10,
    progress: 0,
    ready: !(u as any)._vote_claimed,
    claimed: !!(u as any)._vote_claimed,
    needsVerify: !(u as any)._vote_claimed,
    vote_url: 'https://t.me/tapps_bot/center?startapp=app_cookiecrackerp2e',
  }
  tasks.push(voteTask)

  // Marketplace tasks
  const marketplacePurchases = (u as any).marketplace_purchases || 0
  const marketplaceSales = (u as any).marketplace_sales || 0

  // Buy tasks
  const buyTasks = [
    { id: 'mb5', need: 5, reward: 10, title: 'Buy 5 fortunes in marketplace' },
    { id: 'mb10', need: 10, reward: 25, title: 'Buy 10 fortunes in marketplace' },
    { id: 'mb25', need: 25, reward: 75, title: 'Buy 25 fortunes in marketplace' },
  ]

  buyTasks.forEach(task => {
    const buyTask: TaskItem = {
      id: task.id,
      title: task.title,
      kind: 'marketplace_buy',
      need: task.need,
      reward: task.reward,
      progress: Math.min(marketplacePurchases, task.need),
      ready: marketplacePurchases >= task.need && !(u as any)[`_${task.id}_claimed`],
      claimed: !!(u as any)[`_${task.id}_claimed`],
      needsVerify: false,
    }
    tasks.push(buyTask)
  })

  // Sell tasks
  const sellTasks = [
    { id: 'ms5', need: 5, reward: 15, title: 'Sell 5 fortunes in marketplace' },
    { id: 'ms10', need: 10, reward: 35, title: 'Sell 10 fortunes in marketplace' },
    { id: 'ms25', need: 25, reward: 100, title: 'Sell 25 fortunes in marketplace' },
  ]

  sellTasks.forEach(task => {
    const sellTask: TaskItem = {
      id: task.id,
      title: task.title,
      kind: 'marketplace_sell',
      need: task.need,
      reward: task.reward,
      progress: Math.min(marketplaceSales, task.need),
      ready: marketplaceSales >= task.need && !(u as any)[`_${task.id}_claimed`],
      claimed: !!(u as any)[`_${task.id}_claimed`],
      needsVerify: false,
    }
    tasks.push(sellTask)
  })

  return tasks
}

function sleep(ms: number) {
  return new Promise(res => setTimeout(res, ms))
}

class MockApiService {
  constructor() {
    ensureSeedListings()
  }

  getBaseUrl(): string {
    return API_BASE
  }

  // Simulate a little latency so the UI resembles real behavior
  private async lag() { await sleep(150 + Math.random()*250) }

  async getStatus(userId: string): Promise<GameState> {
    await this.lag()
    const u = ensureUser(userId)
    maybeDailyReset(u)
    saveDb(mockDb)
    return normalize(u)
  }

  // after
async generateFortune(userId: string): Promise<FortuneApiResponse> {
  const u = ensureUser(userId)
  const messages = [
    'You will find great luck today.',
    'A thrilling time is in your immediate future.',
    'Your ability to juggle many tasks will take you far.'
  ]
  const idx = (userId.split('').reduce((a,b) => a + b.charCodeAt(0), 0) + u.total_cracked) % messages.length
  const tiers = ['common','uncommon','rare','epic','legendary','limited']
  const tierIdx = Math.min(Math.floor(u.total_cracked / 20), tiers.length - 1)

  return {
    data: {
      message: messages[idx],
      rarity: { tier: tiers[tierIdx], p: 0, stars: tierStarMap[tiers[tierIdx]] ?? Math.min(tierIdx + 1, 5), color: '' },
      theme: 'default',
      id: `mock-${Date.now()}`,
      seed: Math.floor(Math.random()*1e6),
      tone: 'neutral',
    },
    meta: { status: 200, season: 'dev' }
  }
}



  async getLeaderboard(): Promise<LeaderboardEntry[]> {
    await this.lag()
    const pool = ensureMockPlayerPool()
    const sorted = [...pool]
      .sort((a, b) => b.total_cracked - a.total_cracked)
      .slice(0, 100)
    return sorted.map(player => ({
      id: player.id,
      total_cracked: player.total_cracked,
      telegram_id: player.telegram_id,
      display: player.display,
      username: player.username ? `@${player.username}` : null,
    }))
  }

  async getRivals(userId: string, n: number = 5): Promise<RivalsResponse> {
    await this.lag()
    const u = ensureUser(userId)
    const pool = ensureMockPlayerPool()
    const sorted = [...pool].sort((a, b) => b.total_cracked - a.total_cracked)
    const rankIndex = sorted.findIndex(player => player.id === userId)
    const rank = rankIndex >= 0 ? rankIndex + 1 : sorted.length
    const total = sorted.length

    const abovePlayers = rankIndex > 0 ? sorted.slice(Math.max(0, rankIndex - n), rankIndex) : []
    const belowPlayers = rankIndex >= 0 ? sorted.slice(rankIndex + 1, rankIndex + 1 + n) : sorted.slice(0, n)

    const mapPlayer = (player: MockPlayerProfile) => ({
      id: player.id,
      total_cracked: player.total_cracked,
      telegram_id: player.telegram_id,
      display: player.display,
      username: player.username ? `@${player.username}` : null,
    })

    const you = mapPlayer({
      id: userId,
      telegram_id: userId,
      display: sorted[rankIndex]?.display ?? `Player ${String(userId).slice(-4)}`,
      username: sorted[rankIndex]?.username ?? `player${String(userId).slice(-4)}`,
      total_cracked: u.total_cracked,
    })

    const hasMoreAbove = rankIndex >= 0
      ? rankIndex - abovePlayers.length > 0
      : sorted.length > abovePlayers.length
    const hasMoreBelow = rankIndex >= 0
      ? rankIndex + 1 + belowPlayers.length < sorted.length
      : belowPlayers.length < sorted.length

    return {
      you,
      rank,
      total,
      above: abovePlayers.map(mapPlayer),
      below: belowPlayers.map(mapPlayer),
      has_more_above: hasMoreAbove,
      has_more_below: hasMoreBelow,
    }
  }

  async searchPlayers(query: string, limit: number = 20): Promise<PlayerSearchResult[]> {
    await this.lag()
    const trimmed = query.trim()
    if (!trimmed) return []

    ensureMockPlayerPool()
    Object.values(mockDb).forEach(syncUserToPlayerPool)

    const pool = ensureMockPlayerPool()
    const normalizedTerm = trimmed.toLowerCase()
    const usernameTerm = normalizedTerm.startsWith('@') ? normalizedTerm.slice(1) : normalizedTerm
    const sorted = [...pool].sort((a, b) => b.total_cracked - a.total_cracked)
    const rankMap = new Map<string, number>()
    sorted.forEach((player, index) => {
      rankMap.set(player.id, index + 1)
    })

    const matches = sorted.filter(player => {
      const idMatch = player.id.toLowerCase().includes(normalizedTerm)
      const usernameMatch = player.username.toLowerCase().includes(usernameTerm)
      const displayMatch = player.display.toLowerCase().includes(normalizedTerm)
      return idMatch || usernameMatch || displayMatch
    })

    const max = Math.max(1, Math.min(50, Number(limit) || 20))
    return matches.slice(0, max).map(player => {
      const dbUser = mockDb[player.id]
      return {
        id: player.id,
        display: player.display,
        username: `@${player.username}`,
        link: `tg://user?id=${player.id}`,
        total_cracked: dbUser?.total_cracked ?? player.total_cracked,
        streak_cur: dbUser?.streak_cur ?? 0,
        streak_best: dbUser?.streak_best ?? 0,
        rank: rankMap.get(player.id) ?? null,
        telegram_id: player.telegram_id,
      }
    })
  }

  async getPlayerProfile(
    userId: string,
    options: { limit?: number; offset?: number; sort?: string } = {}
  ): Promise<PlayerProfile> {
    await this.lag()
    const u = ensureUser(userId)
    const pool = ensureMockPlayerPool()
    const player = pool.find(p => p.id === userId)
    const sorted = [...pool].sort((a, b) => b.total_cracked - a.total_cracked)
    const rankIndex = sorted.findIndex(p => p.id === userId)
    const rank = rankIndex >= 0 ? rankIndex + 1 : null

    const limit = options.limit ?? 12
    const offset = options.offset ?? 0
    const sort = options.sort === 'rarity' ? 'rarity' : 'recent'
    const inventory = await this.getInventory(userId, limit, offset, sort as 'rarity' | 'recent')
    const trophySource = Array.isArray(inventory?.items) ? inventory.items : []
    const trophies = getActiveTrophiesFromInventoryItems(trophySource)

    const displayFallback = `Player ${String(userId).slice(-4)}`
    const usernameFallback = `player${String(userId).slice(-4)}`

    return {
      id: userId,
      telegram_id: player?.telegram_id ?? userId,
      display: player?.display ?? displayFallback,
      username: player ? `@${player.username}` : `@${usernameFallback}`,
      link: `tg://user?id=${player?.telegram_id ?? userId}`,
      total_cracked: u.total_cracked,
      streak: {
        current: u.streak_cur ?? 0,
        best: u.streak_best ?? 0,
        last_played: u.last_played ?? null,
      },
      rank,
      total_players: sorted.length,
      stars_balance: (u as any).stars_balance ?? 0,
      trophies,
      fortune_inventory: inventory,
    }
  }

  async createInvoice(_userId: string, packs: number = 1): Promise<{ invoice_url: string }> {
    await this.lag()
    return { invoice_url: `https://example.com/invoice?mock=1&packs=${packs}` }
  }

  async createFeatureInvoice(_userId: string, feature: string): Promise<{ invoice_url: string }> {
    await this.lag()
    return { invoice_url: `https://example.com/feature-invoice?mock=1&feature=${encodeURIComponent(feature)}` }
  }

  async createPreparedShareMessage(_userId: string, _payload: PreparedShareRequest): Promise<PreparedShareResponse> {
    await this.lag()
    return {
      prepared_message_id: `mock-prepared-${Date.now()}`,
      expires_at: Math.floor(Date.now() / 1000) + 3600,
    }
  }

  async attachReferral(userId: string, refCode: string): Promise<void> {
    await this.lag()
    const u = ensureUser(userId)
    if (!u.referred_by) u.referred_by = refCode
    saveDb(mockDb)
  }

  async getTasks(userId: string): Promise<TaskItem[]> {
    await this.lag()
    const u = ensureUser(userId)
    u.tasks = buildTasks(u)
    saveDb(mockDb)
    return u.tasks!
  }

  async getFortune(userId: string): Promise<{ fortune: FortuneDisplay; state: GameState }> {
    await this.lag()
    const u = ensureUser(userId)
    maybeDailyReset(u)
    if (u.cracks_left <= 0) throw new Error('NO_CRACKS_LEFT')

    // spend a crack + streak
    u.cracks_left -= 1
    u.total_cracked += 1
    updateStreakOnCrack(u)
    syncUserToPlayerPool(u)

    // mock fortune
    const msgs = ['Quiet persistence tips the scale.', 'Small sparks light lasting fires.', 'Fresh eyes reveal the shortcut.']
    const idx = (userId.split('').reduce((a,b)=>a+b.charCodeAt(0),0)+u.total_cracked)%msgs.length
    const tiers = ['common','uncommon','rare','epic','legendary','limited']
    const tierIdx = Math.min(Math.floor(u.total_cracked/20), tiers.length-1)

    const fortune: FortuneDisplay = {
      message: msgs[idx],
      tier: tiers[tierIdx],
      stars: tierStarMap[tiers[tierIdx]] ?? Math.min(tierIdx + 1, 5),
    }

    const inventory = ensureInventory(userId, u)
    const mintedAt = new Date().toISOString()
    const newItem: InventoryItem = {
      mint_id: `mint-${userId}-${Date.now()}`,
      minted_at: mintedAt,
      source: 'crack',
      crack_total_after: u.total_cracked,
      streak_cur_after: u.streak_cur ?? 0,
      fortune_id: `fortune-${Date.now()}`,
      message: fortune.message,
      theme: 'default',
      tone: 'positive',
      rarity_tier: fortune.tier,
      stars: fortune.stars,
      p: fortune.stars > 1 ? Math.round(Math.random() * 100) / 1000 : null,
      color: mockRarityColors[fortune.tier] ?? '#CD853F',
      season: 'dev',
      listing_id: null,
      listing_price_stars: null,
      listing_created_at: null,
    }
    inventory.unshift(newItem)

    saveDb(mockDb)
    return { fortune, state: normalize(u) }
  }
  async claimTask(userId: string, taskId: string): Promise<GameState> {
    await this.lag()
    const u = ensureUser(userId)
    // compute tasks to know readiness
    const tasks = buildTasks(u)
    const t = tasks.find(t => t.id === taskId)
    if (!t) throw new Error('Task not found')

    if (t.kind === 'counter' && t.id === 'counter:10') {
      if (!(u as any)._counter10_claimed && t.ready) {
        (u as any)._counter10_claimed = true
        u.cracks_left += t.reward
      } else {
        throw new Error('Task not ready or already claimed')
      }
    } else if (t.kind === 'streak' && t.id === 'streak:3') {
      if (!(u as any)._streak3_claimed && t.ready) {
        (u as any)._streak3_claimed = true
        u.cracks_left += t.reward
      } else {
        throw new Error('Task not ready or already claimed')
      }
    } else if (t.kind === 'bot_started' && t.id === 'tg:bot_started') {
      if (!(u as any)._bot_started_claimed) {
        (u as any)._bot_started_claimed = true
        u.cracks_left += t.reward
      } else {
        throw new Error('Task already claimed')
      }
    } else if (t.kind === 'tg_member' && t.id === 'tg:join_channel') {
      if (!(u as any)._tg_join_verified) throw new Error('Verification required')
      if (!(u as any)._tg_join_claimed) {
        (u as any)._tg_join_claimed = true
        u.cracks_left += t.reward
      } else {
        throw new Error('Task already claimed')
      }
    } else if (t.kind === 'apps_center_vote' && t.id === 'apps_center:vote') {
      if (!(u as any)._vote_claimed) {
        (u as any)._vote_claimed = true
        u.cracks_left += t.reward
      } else {
        throw new Error('Task already claimed')
      }
    } else if (t.kind === 'marketplace_buy' || t.kind === 'marketplace_sell') {
      // Handle marketplace tasks
      const claimKey = `_${t.id}_claimed`
      if (!(u as any)[claimKey] && t.ready) {
        (u as any)[claimKey] = true
        u.cracks_left += t.reward
      } else {
        throw new Error('Task not ready or already claimed')
      }
    } else {
      throw new Error('Unsupported task in mock')
    }

    saveDb(mockDb)
    return normalize(u)
  }

  async verifyTask(userId: string, taskId: string): Promise<GameState> {
    await this.lag()
    const u = ensureUser(userId)
    if (taskId === 'tg:join_channel') {
      ;(u as any)._tg_join_verified = true
    } else if (taskId === 'apps_center:vote') {
      // For voting task, we'll mark it as verified and claimed in one step
      ;(u as any)._vote_claimed = true
      u.cracks_left += 10 // Add the reward
    } else {
      throw new Error('Nothing to verify for this task in mock')
    }
    saveDb(mockDb)
    return normalize(u)
  }

  async getInventory(userId: string, limit: number = 20, offset: number = 0, sort: 'rarity' | 'recent' = 'recent'): Promise<InventoryResponse> {
    await this.lag()
    const u = ensureUser(userId)
    const items = [...ensureInventory(userId, u)]
    let changed = false

    items.forEach((item) => {
      if (item.listing_id) {
        const listing = mockListings[item.listing_id]
        if (!listing || !listing.active) {
          item.listing_id = null
          item.listing_price_stars = null
          item.listing_created_at = null
          changed = true
        }
      }
    })

    if (changed) {
      saveDb(mockDb)
    }

    if (sort === 'rarity') {
      items.sort((a, b) => {
        if (a.stars !== b.stars) return b.stars - a.stars
        if (a.p !== b.p) return (a.p || 1) - (b.p || 1)
        return new Date(b.minted_at).getTime() - new Date(a.minted_at).getTime()
      })
    } else {
      items.sort((a, b) => new Date(b.minted_at).getTime() - new Date(a.minted_at).getTime())
    }

    const paginatedItems = items.slice(offset, offset + limit)

    return {
      items: paginatedItems,
      meta: {
        total: items.length,
        limit,
        offset,
        sort
      }
    }
  }

  async burnFortune(userId: string, mintId: string): Promise<{ ok: boolean }> {
    await this.lag()
    const u = ensureUser(userId)
    const inventory = ensureInventory(userId, u)
    const index = inventory.findIndex((item) => item.mint_id === mintId)
    if (index === -1) {
      throw new Error('Fortune not found')
    }

    const [removed] = inventory.splice(index, 1)
    if (removed.listing_id) {
      delete mockListings[removed.listing_id]
      saveListings(mockListings)
    }

    saveDb(mockDb)
    return { ok: true }
  }

  async claimRewardedAdReward(userId: string): Promise<string> {
    await this.lag()
    const u = ensureUser(userId)
    const today = todayKey()
    
    console.log(`[MOCK] Before reward claim:`, {
      userId,
      today,
      lastAdReward: u.last_ad_reward,
      cracksLeft: u.cracks_left
    })
    
    if (u.last_ad_reward === today) {
      throw new Error('AD_REWARD_ALREADY_CLAIMED_TODAY')
    }
    
    u.cracks_left += 1
    u.last_ad_reward = today
    saveDb(mockDb)
    
    console.log(`[MOCK] After reward claim:`, {
      userId,
      today,
      lastAdReward: u.last_ad_reward,
      cracksLeft: u.cracks_left
    })
    
    return 'OK'
  }

  async getMarketplaceListings(
    limit: number = 20,
    offset: number = 0,
    sort: MarketplaceSort = 'newest',
    seller?: string
  ): Promise<MarketplaceResponse> {
    await this.lag()
    ensureSeedListings()

    const listings = Object.values(mockListings).filter((listing) => {
      if (!listing.active) return false
      if (seller) return listing.seller_id === seller
      return true
    })

    const sorted = listings.sort((a, b) => {
      const createdA = new Date(a.created_at).getTime()
      const createdB = new Date(b.created_at).getTime()
      const priceA = a.price_sparkles || a.price_stars || 0
      const priceB = b.price_sparkles || b.price_stars || 0
      const priceDiff = priceA - priceB
      const priceDiffDesc = priceB - priceA
      const rarityDiffAsc = a.stars - b.stars
      const rarityDiffDesc = b.stars - a.stars
      const probA = a.p ?? 1
      const probB = b.p ?? 1

      switch (sort) {
        case 'priceasc':
          return priceDiff !== 0 ? priceDiff : createdA - createdB
        case 'pricedesc':
          return priceDiffDesc !== 0 ? priceDiffDesc : createdB - createdA
        case 'rarityasc':
          if (rarityDiffAsc !== 0) return rarityDiffAsc
          if (probA !== probB) return probB - probA
          return createdB - createdA
        case 'raritydesc':
          if (rarityDiffDesc !== 0) return rarityDiffDesc
          if (probA !== probB) return probA - probB
          return createdB - createdA
        case 'oldest':
          return createdA - createdB
        case 'newest':
        default:
          return createdB - createdA
      }
    })

    const slice = sorted.slice(offset, offset + limit)
    return {
      items: slice.map((listing) => ({ ...listing })),
      meta: {
        total: sorted.length,
        limit,
        offset,
        sort,
        seller: seller ?? null,
      },
    }
  }

  async listFortuneForSale(userId: string, mintId: string, priceStars: number): Promise<MarketplaceListing> {
    await this.lag()
    if (!Number.isFinite(priceStars) || priceStars <= 0) {
      throw new Error('Invalid price')
    }

    const u = ensureUser(userId)
    const inventory = ensureInventory(userId, u)
    const item = inventory.find((entry) => entry.mint_id === mintId)
    if (!item) {
      throw new Error('Fortune not found')
    }
    if (item.listing_id) {
      throw new Error('Fortune already listed')
    }

    const listingId = `listing-${Date.now()}-${Math.floor(Math.random() * 1000)}`
    const createdAt = new Date().toISOString()

    const listing: MockListing = {
      id: listingId,
      active: true,
      mint_id: mintId,
      seller_id: userId,
      seller_username: `user_${userId.slice(-4)}`,
      seller_display_name: `User ${userId.slice(-4)}`,
      price_stars: Math.round(priceStars),
      created_at: createdAt,
      rarity_tier: item.rarity_tier,
      stars: item.stars,
      p: item.p,
      color: item.color,
      season: item.season,
      message: item.message,
      theme: item.theme,
      tone: item.tone,
    }

    mockListings[listingId] = listing
    item.listing_id = listingId
    item.listing_price_stars = listing.price_stars
    item.listing_created_at = createdAt

    saveDb(mockDb)
    saveListings(mockListings)
    return { ...listing }
  }

  async cancelListing(userId: string, listingId: string): Promise<{ ok: boolean }> {
    await this.lag()
    const listing = mockListings[listingId]
    if (!listing || !listing.active) {
      throw new Error('Listing not found')
    }
    if (listing.seller_id !== userId) {
      throw new Error('Cannot cancel another seller\'s listing')
    }

    listing.active = false
    const u = ensureUser(userId)
    const inventory = ensureInventory(userId, u)
    const item = inventory.find((entry) => entry.mint_id === listing.mint_id)
    if (item) {
      item.listing_id = null
      item.listing_price_stars = null
      item.listing_created_at = null
    }

    delete mockListings[listingId]
    saveDb(mockDb)
    saveListings(mockListings)
    return { ok: true }
  }

  async getMarketplaceInvoice(userId: string, listingId: string): Promise<{ invoice_url: string }> {
    await this.lag()
    const listing = mockListings[listingId]
    if (!listing || !listing.active) {
      throw new Error('Listing not available')
    }
    if (listing.seller_id === userId) {
      throw new Error('Cannot buy your own listing')
    }

    return {
      invoice_url: `https://example.com/marketplace-invoice?mock=1&listing=${encodeURIComponent(listingId)}`,
    }
  }

  // Sparkles methods
  async getSparklesBalance(userId: string): Promise<SparklesBalance> {
    await this.lag()
    const u = ensureUser(userId)
    return {
      sparkles_balance: u.sparkles_balance || 0,
    }
  }

  async createSparklesInvoice(userId: string, starsAmount: number): Promise<{ invoice_link: string }> {
    await this.lag()
    return {
      invoice_link: `https://example.com/sparkles-invoice?mock=1&stars=${starsAmount}&user=${encodeURIComponent(userId)}`,
    }
  }

  async getSparklesHistory(_userId: string, _limit?: number, _offset?: number): Promise<SparklesHistory> {
    await this.lag()
    return {
      transactions: [],
      meta: {
        total: 0,
        limit: _limit || 50,
        offset: _offset || 0,
      }
    }
  }

  async listFortuneForSparkles(userId: string, mintId: string, priceSparkles: number): Promise<MarketplaceListing> {
    await this.lag()
    if (!Number.isFinite(priceSparkles) || priceSparkles <= 0) {
      throw new Error('Invalid price')
    }

    const u = ensureUser(userId)
    const inventory = ensureInventory(userId, u)
    const item = inventory.find((entry) => entry.mint_id === mintId)
    if (!item) {
      throw new Error('Fortune not found')
    }
    if (item.listing_id) {
      throw new Error('Fortune already listed')
    }

    const listingId = `listing-${Date.now()}-${Math.floor(Math.random() * 1000)}`
    const createdAt = new Date().toISOString()

    const listing: MockListing = {
      id: listingId,
      active: true,
      mint_id: mintId,
      seller_id: userId,
      seller_username: `user_${userId.slice(-4)}`,
      seller_display_name: `User ${userId.slice(-4)}`,
      price_sparkles: Math.round(priceSparkles),
      currency_type: 'sparkles',
      created_at: createdAt,
      rarity_tier: item.rarity_tier,
      stars: item.stars,
      p: item.p,
      color: item.color,
      season: item.season,
      message: item.message,
      theme: item.theme,
      tone: item.tone,
    }

    mockListings[listingId] = listing
    item.listing_id = listingId
    item.listing_price_sparkles = listing.price_sparkles
    item.listing_created_at = createdAt

    saveDb(mockDb)
    saveListings(mockListings)
    return { ...listing }
  }

  async purchaseListingWithSparkles(userId: string, listingId: string): Promise<{ ok: boolean }> {
    await this.lag()
    const listing = mockListings[listingId]
    if (!listing || !listing.active) {
      throw new Error('Listing not found or inactive')
    }
    if (listing.seller_id === userId) {
      throw new Error('Cannot buy your own listing')
    }

    const buyer = ensureUser(userId)
    const seller = ensureUser(listing.seller_id)
    const buyerInventory = ensureInventory(userId, buyer)
    const sellerInventory = ensureInventory(listing.seller_id, seller)

    const price = listing.price_sparkles || 0
    if ((buyer.sparkles_balance || 0) < price) {
      throw new Error('Insufficient sparkles balance')
    }

    const sellerItem = sellerInventory.find((item) => item.mint_id === listing.mint_id)
    if (!sellerItem || sellerItem.listing_id !== listingId) {
      throw new Error('Item no longer available')
    }

    // Transfer sparkles
    buyer.sparkles_balance = (buyer.sparkles_balance || 0) - price
    seller.sparkles_balance = (seller.sparkles_balance || 0) + price

    // Track marketplace activity
    ;(buyer as any).marketplace_purchases = ((buyer as any).marketplace_purchases || 0) + 1
    ;(seller as any).marketplace_sales = ((seller as any).marketplace_sales || 0) + 1

    // Transfer item
    sellerInventory.splice(sellerInventory.indexOf(sellerItem), 1)
    buyerInventory.push({
      ...sellerItem,
      listing_id: undefined,
      listing_price_stars: undefined,
      listing_price_sparkles: undefined,
      listing_created_at: undefined,
    })

    // Deactivate listing
    listing.active = false

    saveDb(mockDb)
    saveListings(mockListings)
    return { ok: true }
  }
}

// ===============================
// Factory + export
// ===============================
function createApiService() {
  if (USE_MOCK) return new MockApiService()
  return new RealApiService()
}

export const apiService = createApiService()

// ===============================
// Optional helpers for dev
// ===============================
export function getDevUserId(): string {
  if (typeof window === 'undefined') return 'dev-user'
  // Prefer Telegram user id when available
  const tgId = window.Telegram?.WebApp?.initDataUnsafe?.user?.id
  if (tgId) return String(tgId)
  // Else, sticky local id
  const k = 'mockApi:devUserId'
  let id = window.localStorage.getItem(k)
  if (!id) { id = String(100000 + Math.floor(Math.random()*900000)); window.localStorage.setItem(k, id) }
  return id
}


// ========================================================================
// OPTIONAL (separate file): dev-telegram.ts — super tiny Telegram WebApp polyfill
// ========================================================================
// Create src/dev-telegram.ts with the following code, then import it early in
// your app (e.g., in main.ts) so components that touch window.Telegram don't
// explode during local dev.
//
// import './dev-telegram' // do this only in dev if you prefer
//
// Code:
/*
if (typeof window !== 'undefined') {
  const isDev = (typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV) ?? false
  if (isDev && !window.Telegram) {
    (window as any).Telegram = {
      WebApp: {
        initData: '',
        initDataUnsafe: { user: { id: '999999' }, start_param: '' },
        ready: () => console.log('[mock TG] ready'),
        onEvent: (_e: string, _cb: () => void) => {},
        openInvoice: (url: string, cb: (status: string) => void) => {
          console.log('[mock TG] openInvoice →', url)
          setTimeout(() => cb('paid'), 500)
        },
        version: '9.0',
        openTelegramLink: (url: string) => window.open(url, '_blank'),
        expand: () => {},
        viewportHeight: window.innerHeight,
        viewportStableHeight: window.innerHeight,
        setHeaderColor: () => {},
        setBackgroundColor: () => {},
      },
    }
    console.info('[dev] Injected Telegram WebApp mock')
  }
}
*/
